/**
 * The help desk queue.
 *
 * Legacy's list screen: the status cards above it, the same columns in the same
 * order, the archive, and a row menu that can reopen a closed ticket without
 * opening it. What it does not carry over is the inline Status and Priority
 * dropdowns that sat in every row — the same change is one click away on the
 * ticket itself, and a table where seven of the cells are live form controls is
 * a table where a mis-click is a save.
 *
 * The reference is the way in, and it goes to a page rather than a panel: a
 * ticket has five tabs and a conversation, which is not a thing to read in a
 * drawer over the list it came from.
 */

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { ListTree, MessageCircle, Paperclip, RotateCcw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

import { TicketForm } from "@/components/helpdesk/ticket-form";
import { TicketSummary } from "@/components/helpdesk/ticket-summary";
import {
  type ColumnDef,
  Mono,
  type ResourceConfig,
  ResourcePage,
} from "@/components/resource-page";
import { Badge } from "@/components/ui/badge";
import { api, errorMessage, ifMatch } from "@/services/api";
import type { Translate } from "@/constants/i18n";
import {
  ASSIGNMENT_LABEL,
  DEPARTMENT_LABEL,
  DEPARTMENTS,
  HELPDESK_KEY,
  TICKETS_ENDPOINT,
  isClosed,
  PRIORITIES,
  PRIORITY_LABEL,
  PRIORITY_TONE,
  STATUS_LABEL,
  STATUS_TONE,
  STATUSES,
  type Ticket,
  type TicketRow,
  type TicketStatus,
  TYPE_LABEL,
  TYPES,
} from "@/services/helpdesk";
import { useTranslation } from "@/store/locale";

/** The columns, in the order legacy read them. */
const columns = (t: Translate): ColumnDef<TicketRow>[] => [
  {
    key: "reference",
    label: t("hd.reference"),
    sortKey: "id",
    render: (row) => (
      <Link
        to={`/helpdesk/${row.id}`}
        className="text-accent font-medium hover:underline hover:underline-offset-4"
      >
        <Mono>{row.reference}</Mono>
      </Link>
    ),
  },
  {
    key: "title",
    label: t("hd.title"),
    render: (row) => <span className="text-fg">{row.title}</span>,
  },
  {
    key: "priority",
    label: t("hd.priority"),
    sortKey: "priority",
    render: (row) => (
      <Badge tone={PRIORITY_TONE[row.priority]}>{t(PRIORITY_LABEL[row.priority])}</Badge>
    ),
  },
  {
    key: "status",
    label: t("hd.status"),
    sortKey: "status",
    render: (row) => (
      <Badge tone={STATUS_TONE[row.status]}>{t(STATUS_LABEL[row.status])}</Badge>
    ),
  },
  {
    key: "due_date",
    label: t("hd.dueDate"),
    sortKey: "due_date",
    render: (row) => {
      if (!row.due_date) return "—";
      // Late and still open is the one thing a queue has to show at a glance.
      const late = !isClosed(row.status) && new Date(row.due_date) < new Date();
      return (
        <span className={late ? "text-danger font-medium" : undefined}>
          {format(new Date(row.due_date), "MM/dd/yyyy")}
        </span>
      );
    },
  },
  {
    key: "ticket_type",
    label: t("hd.type"),
    render: (row) => (row.ticket_type ? t(TYPE_LABEL[row.ticket_type]) : "—"),
  },
  {
    key: "department",
    label: t("hd.department"),
    render: (row) => (row.department ? t(DEPARTMENT_LABEL[row.department]) : "—"),
  },
  {
    key: "assignment",
    label: t("hd.assignTo"),
    render: (row) => t(ASSIGNMENT_LABEL[row.assignment]),
  },
  {
    key: "assignee_name",
    label: t("hd.handler"),
    fields: ["assignee_name", "assignee_id"],
    render: (row) => row.assignee_name ?? "—",
  },
  {
    key: "activity",
    label: t("hd.activity"),
    fields: ["tasks", "comment_count", "attachment_count", "has_unread_comments"],
    // Three numbers that would each be a nearly empty column of their own.
    // Together they are the one thing a queue is scanned for after status:
    // which tickets have something going on. The dot is legacy's — a ticket
    // with lines on it this account has not seen.
    render: (row) => (
      <span className="text-fg-muted flex items-center gap-3">
        <span className="flex items-center gap-1" title={t("hd.tasks")}>
          <ListTree className="size-3.5" aria-hidden />
          {row.tasks.length}
        </span>
        <span className="flex items-center gap-1" title={t("hd.tabComments")}>
          <span className="relative flex">
            <MessageCircle className="size-3.5" aria-hidden />
            {row.has_unread_comments ? (
              <span
                className="bg-accent absolute -top-0.5 -right-0.5 size-1.5 rounded-full"
                aria-label={t("hd.unread")}
              />
            ) : null}
          </span>
          {row.comment_count}
        </span>
        <span className="flex items-center gap-1" title={t("hd.attachments")}>
          <Paperclip className="size-3.5" aria-hidden />
          {row.attachment_count}
        </span>
      </span>
    ),
  },
  {
    key: "created_by_name",
    label: t("hd.raisedBy"),
    render: (row) => row.created_by_name ?? "—",
  },
  {
    key: "created_at",
    label: t("hd.raisedOn"),
    sortKey: "created_at",
    render: (row) => format(new Date(row.created_at), "MM/dd/yyyy h:mm a"),
  },
];

export default function HelpdeskPage() {
  const t = useTranslation();
  const queryClient = useQueryClient();

  // Reopening from the list, which legacy offered in the row menu and is the
  // one change worth making without opening the ticket: a resolved ticket
  // somebody has come back about should not need five clicks to be live again.
  const reopen = useMutation({
    mutationFn: async (row: TicketRow) =>
      api.patch(`/helpdesk/tickets/${row.id}`, { status: "reopen" }, ifMatch(row)),
    onSuccess: async () => {
      toast.success(t("hd.reopened"));
      await queryClient.invalidateQueries({ queryKey: HELPDESK_KEY });
      await queryClient.invalidateQueries({ queryKey: [TICKETS_ENDPOINT] });
    },
    onError: (failure) => toast.error(errorMessage(failure, t("hd.saveFailed"))),
  });

  const config: ResourceConfig<TicketRow> = {
    title: t("nav.helpdesk"),
    description: t("hd.pageDescription"),
    singular: t("hd.ticket"),
    plural: t("hd.tickets"),
    endpoint: "/helpdesk/tickets",
    model: "helpdesk_ticket",
    defaultSort: { key: "created_at", order: "desc" },
    searchPlaceholder: t("hd.searchPlaceholder"),
    searchMaxLength: 200,
    columns: columns(t),
    // The cards are the summary and the status filter at once.
    banner: (values, apply) => (
      <TicketSummary
        active={(values.status as TicketStatus | undefined) ?? null}
        overdue={values.overdue === "true"}
        onPick={(status) => {
          // A status and "past due" are two answers to the same question —
          // what is on screen — so pressing one card puts the other down.
          const next = { ...values };
          delete next.overdue;
          if (status === null) delete next.status;
          else next.status = status;
          apply(next);
        }}
        onPickOverdue={() => {
          const next = { ...values };
          delete next.status;
          if (values.overdue === "true") delete next.overdue;
          else next.overdue = "true";
          apply(next);
        }}
      />
    ),
    filters: [
      {
        name: "status",
        label: t("hd.status"),
        type: "select",
        options: STATUSES.map((value) => ({ value, label: t(STATUS_LABEL[value]) })),
      },
      {
        name: "priority",
        label: t("hd.priority"),
        type: "select",
        options: PRIORITIES.map((value) => ({ value, label: t(PRIORITY_LABEL[value]) })),
      },
      {
        name: "ticket_type",
        label: t("hd.type"),
        type: "select",
        options: TYPES.map((value) => ({ value, label: t(TYPE_LABEL[value]) })),
      },
      {
        name: "department",
        label: t("hd.department"),
        type: "select",
        options: DEPARTMENTS.map((value) => ({ value, label: t(DEPARTMENT_LABEL[value]) })),
      },
      {
        name: "assignee_id",
        label: t("hd.handler"),
        type: "fk",
        endpoint: "/helpdesk/employees/compact",
      },
      { name: "created_from", label: t("geo.createdFrom"), type: "date" },
      { name: "created_to", label: t("geo.createdTo"), type: "date" },
      { name: "due_from", label: t("hd.dueFrom"), type: "date" },
      { name: "due_to", label: t("hd.dueTo"), type: "date" },
      { name: "overdue", label: t("hd.overdue"), type: "boolean" },
    ],
    // The form is its own component — a ticket is more than a field list, and
    // the same sheet raises one from here and adds a task from the ticket page.
    fields: [],
    toForm: () => ({}),
    // `row` is the whole ticket, not the list row: ResourcePage reads the
    // record through `GET {endpoint}/{id}` before it opens anything, precisely
    // so a form is never filled in from a projection. The cast says that.
    renderForm: (row, onClose) => (
      <TicketForm
        ticket={row === "new" ? null : (row as unknown as Ticket)}
        onClose={onClose}
      />
    ),
    formVariant: "modal",
    // The reference opens the ticket's own page, so there is no panel to open
    // in place of it. The row menu keeps Edit, Delete and Reopen.
    noView: true,
    addLabel: t("hd.raiseTicket"),
    toolbarLayout: "split",
    // Archived rather than deleted, both halves behind the same right — and
    // the ticked rows too, which is how legacy's table worked.
    archiveFilter: { name: "archived", value: "true", permission: "delete__helpdesk_ticket" },
    restore: { permission: "delete__helpdesk_ticket" },
    bulk: { permission: "delete__helpdesk_ticket" },
    confirmDeleteMessage: t("hd.confirmArchive"),
    rowActions: (row) =>
      isClosed(row.status)
        ? [{ label: t("hd.reopen"), icon: RotateCcw, run: () => reopen.mutate(row) }]
        : [],
  };

  return <ResourcePage config={config} />;
}
